// apps/.../wet/components/Hero.tsx
'use client';
import React, { useEffect, useRef, useState } from 'react';

/** WP endpoint (env override supported) */
const WP_MARKETING_ENDPOINT =
  process.env.NEXT_PUBLIC_WET_WP_ENDPOINT ||
  'https://wateredgetech.com/wp-json/wet/v1/marketing';

/** GA helper */
function ga(event: string, params?: Record<string, unknown>) {
  try {
    if (typeof window === 'undefined') return;
    if (window.top !== window.self) return; // parent-only iframes: keep this
    const w: any = window;
    if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({ event, ...(params || {}) });
      return;
    }
    // Fallback if gtag is still present in older deploys
    if (typeof w.gtag === 'function') {
      w.gtag('event', event, params || {});
    }
  } catch {}
}


/** Allow a tiny subset of inline tags from WP */
function sanitizeInline(html: string) {
  try {
    const el = document.createElement('div');
    el.innerHTML = html;
    // strip disallowed tags, keep <strong><em><br>
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_ELEMENT, null);
    const allowed = new Set(['STRONG', 'EM', 'BR']);
    const toRemove: Element[] = [];
    while (walker.nextNode()) {
      const n = walker.currentNode as Element;
      if (!allowed.has(n.tagName)) {
        // unwrap node: move children up, then schedule removal
        while (n.firstChild) n.parentNode?.insertBefore(n.firstChild, n);
        toRemove.push(n);
      }
      // strip all attributes
      [...(n.attributes || [])].forEach((a) => n.removeAttribute(a.name));
    }
    toRemove.forEach((n) => n.remove());
    return el.innerHTML;
  } catch {
    return html.replace(/<[^>]+>/g, '');
  }
}

type HeroCopy = {
  title?: string;        // may include <strong>/<em>/<br>
  sub?: string;          // may include <strong>/<em>/<br>
  cta?: { text?: string; href?: string };
};

export default function Hero() {
  // Defaults (safe + sensible)
  const [copy, setCopy] = useState<HeroCopy>({
    title: 'Water softeners in San Antonio, zero hassle.',
    sub: 'Upgrade to soft water with Water Edge Technologies for cleaner fixtures, softer laundry, and better-tasting water throughout your home.',
    cta: { text: 'Schedule a water test', href: '#why-soft-water' },
  });

  // Pull text from WP (supports either .hero.title/sub or .hero.title_html/sub_html)
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch(WP_MARKETING_ENDPOINT, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          signal: ctrl.signal,
        });
        if (!r.ok) return;
        const data = await r.json();
        const hero = data?.hero || {};
        if (cancelled) return;

        const titleRaw: string =
          (hero.title_html as string) ??
          (hero.title as string) ??
          '';
        const subRaw: string =
          (hero.sub_html as string) ??
          (hero.sub as string) ??
          '';
        const ctaText: string = hero?.cta?.text ?? '';
        const ctaHref: string = hero?.cta?.href ?? '';

        setCopy((prev) => ({
          title: titleRaw ? sanitizeInline(titleRaw) : prev.title,
          sub: subRaw ? sanitizeInline(subRaw) : prev.sub,
          cta: {
            text: ctaText || prev.cta?.text,
            href: ctaHref || prev.cta?.href,
          },
        }));
      } catch {
        /* keep defaults */
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, []);

  // Video analytics
  const vref = useRef<HTMLVideoElement | null>(null);
  const fired = useRef<{ start?: boolean; q25?: boolean; q50?: boolean; q75?: boolean; complete?: boolean }>({});

  useEffect(() => {
    const v = vref.current;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    requestAnimationFrame(() => void v.play().catch(() => {}));

    const onPlay = () => {
      if (!fired.current.start) {
        ga('video_start', { video_title: 'Hero background', video_url: '/blue_hero.mp4', position: 0, location: 'hero' });
        fired.current.start = true;
      }
    };
    const onTime = () => {
      const d = v.duration || 0;
      if (!d || !isFinite(d)) return;
      const q = v.currentTime / d;
      if (q >= 0.25 && !fired.current.q25) { ga('video_progress', { video_title: 'Hero background', video_url: '/blue_hero.mp4', progress: '25', location: 'hero' }); fired.current.q25 = true; }
      if (q >= 0.5  && !fired.current.q50) { ga('video_progress', { video_title: 'Hero background', video_url: '/blue_hero.mp4', progress: '50', location: 'hero' }); fired.current.q50 = true; }
      if (q >= 0.75 && !fired.current.q75) { ga('video_progress', { video_title: 'Hero background', video_url: '/blue_hero.mp4', progress: '75', location: 'hero' }); fired.current.q75 = true; }
    };
    const onEnded = () => {
      if (!fired.current.complete) {
        ga('video_complete', { video_title: 'Hero background', video_url: '/blue_hero.mp4', location: 'hero' });
        fired.current.complete = true;
      }
    };

    v.addEventListener('play', onPlay);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('ended', onEnded);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('ended', onEnded);
    };
  }, []);

  const handleCta = () => {
    ga('select_content', {
      content_type: 'link',
      item_id: 'hero_primary_cta',
      location: 'hero',
      link_url: copy.cta?.href,
      link_text: copy.cta?.text,
    });
  };

  return (
    <section
      id="home"
      className="relative h-screen overflow-hidden flex items-center justify-center text-center text-white"
    >
      <video
        ref={vref}
        className="absolute inset-0 w-full h-full object-cover object-center"
        src="/blue_hero.mp4"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster="/wet/wet-og.png"
        aria-hidden="true"
        tabIndex={-1}
        controls={false}
        controlsList="nodownload noremoteplayback noplaybackrate nofullscreen"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-[#0d1b2a]/85" />

      <div className="relative z-10 p-4 animate-[fadeInRippleText_2s_cubic-bezier(0.25,1,0.5,1)_forwards]">
        <h1
          className="text-5xl md:text-7xl font-bold leading-tight mb-6"
          // allow small inline emphasis from WP
          dangerouslySetInnerHTML={{ __html: copy.title || '' }}
        />
        <p
          className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto mb-10"
          dangerouslySetInnerHTML={{ __html: copy.sub || '' }}
        />
        <a
          href={copy.cta?.href || '#why-soft-water'}
          className="glow-button text-white font-bold px-10 py-4 rounded-full text-lg inline-block"
          onClick={handleCta}
        >
          {copy.cta?.text || 'Discover the Benefits'}
        </a>
      </div>
    </section>
  );
}
